import { type FetchError, FetchErrorReason } from '@cdc-internal/fe-toolbox/libs/fetcher'
import { isServer, QueryClient, type QueryClientConfig } from '@tanstack/react-query'

type QueryKey = [
  (
    | 'coins'
    | 'coinDetails'
    | 'globalMetrics'
    | 'coinMarket'
    | 'allCoins'
    | 'coinPricesBySlug'
    | 'coinPricesById'
    | 'coinBySlug'
    | 'coinStatistics'
    | 'exchangeRates'
    | 'socialData'
    | 'socialNews'
    | 'socialVideos'
    | 'coinMarketPair'
    | 'marketCdcPair'
    | 'cryptoBasket'
    | 'yieldProducts'
    | 'priceAllCoins'
    | 'topMovers'
    | 'trendingCoins'
    | 'topWatchList'
    | 'popularCoins'
    | 'globalMetrics'
    | 'categories'
    | 'cmsTrendingCoins'
    | 'coinLocaleDesc'
    | 'cmsCoinDisclosure'
    | 'fearGreedIndex'
    | 'coinCategories'
    | 'coinCategoryDetail'
    | 'coinsByCategory'
    | 'allStocks'
    | 'stockPricesById'
    | 'stockRatingsById'
    | 'stockRatingsBySymbol'
    | 'stockBySymbol'
    | 'stockLastPrice'
    | 'stocksContent'
    | 'coinsAILatestNews'
    | 'tokenizedStocks'
  ),
  ...ReadonlyArray<unknown>,
]

declare module '@tanstack/react-query' {
  interface Register {
    defaultError: FetchError
    queryKey: QueryKey
    mutationKey: QueryKey
  }
}

const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      // Server: Longer staleTime for cache sharing across requests
      // Client: Shorter staleTime for fresh market data
      staleTime: isServer ? 5 * 60 * 1000 : 60 * 1000,

      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,

      // Retry once on network and timeout errors
      retry: (failureCount, error) =>
        failureCount < 2 &&
        (error.reason === FetchErrorReason.NetworkError ||
          error.reason === FetchErrorReason.Timeout),
    },
  },
}

// Refer to https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr#experimental-streaming-without-prefetching-in-nextjs

const makeQueryClient = () => new QueryClient(queryClientConfig)

let browserQueryClient: QueryClient | undefined = undefined

// Independent query client across all requests to prevent constantly increasing query cache size
// TODO: Shared query client with public data for server and client across all requests
export function getQueryClient() {
  if (isServer) {
    // Server: always make a new query client
    return makeQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) browserQueryClient = makeQueryClient()
    return browserQueryClient
  }
}
